import React, { useState } from 'react';
import {
  MenuItem,
  Typography,
  Box,
  Menu,
  Chip,
  InputBase,
  Paper,
  Divider,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Organization } from '../../types/organization';
import OrgChipIcon from '../../components/styled_api/OrgChipIcon';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import CreateOrganizationDialog from './CreateOrganizationDialog';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import LeakAddOutlinedIcon from '@mui/icons-material/LeakAddOutlined';
import { isSuperadmin } from '../../utilities/superadmin';
import SuperAdminTooltip from '../../components/utilities/SuperAdminTooltip';

const ChangeOrganization: React.FC = () => {
  const {
    userOrgs,
    organization,
    changeOrganization,
    setActivePage,
    user,
    refreshUserOrganizations,
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [switchAnchorEl, setSwitchAnchorEl] = useState<HTMLElement | null>(null);
  const [orgCreateDialog, setOrgCreateDialog] = useState(false);
  const [creationType, setCreationType] = useState('');
  const [isOpeningOrgMenu, setIsOpeningOrgMenu] = useState(false);

  const orgMenuOpen = Boolean(anchorEl);
  const switchMenuOpen = Boolean(switchAnchorEl);

  const isSuperAdmin = isSuperadmin(user);

  const handleOrgMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (isOpeningOrgMenu) return;

    const anchorTarget = event.currentTarget;
    setIsOpeningOrgMenu(true);

    void refreshUserOrganizations()
      .catch(() => {
        // noop: user-facing errors are handled by context toast
      })
      .finally(() => {
        setAnchorEl(anchorTarget);
        setIsOpeningOrgMenu(false);
      });
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleSwitchMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // keep parent menu open
    setSwitchAnchorEl(event.currentTarget);
  };
  const handleSwitchMenuClose = () => {
    setSwitchAnchorEl(null);
  }
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleOrganizationSelect = (org: Organization) => {
    changeOrganization(org.id); // Use changeOrganization to update state and localStorage
    handleMenuClose(); // Close the dropdown after selection
    handleSwitchMenuClose();
  };

  // handle clicking on org creation or org demo creation
  const handleOrgCreate = (type: string) => {
    setOrgCreateDialog(true);
    setCreationType(type);
  };

  // Filter organizations based on search term (name and aliases)
  const filteredOrgs = (Array.isArray(userOrgs) ? userOrgs : []).filter(
    (org: Organization) => {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = org.name.toLowerCase().includes(searchLower);
      const aliasMatch =
        org.aliases?.some((alias: string) =>
          alias.toLowerCase().includes(searchLower)
        ) || false;
      return nameMatch || aliasMatch;
    }
  );

  return (
    <Box>
      <SuperAdminTooltip
        title={organization?.id ? `org_id: ${organization.id}` : undefined}
      >
        <Chip
          className={orgMenuOpen ? 'chipGrey' : 'chipWhiteBorder'}
          onClick={handleOrgMenu}
          disabled={isOpeningOrgMenu}
          icon={<OrgChipIcon organization={organization} size='medium' />}
          label={organization?.name}
        />
      </SuperAdminTooltip>
      <Menu
        sx={{
          display: 'flex',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableAutoFocusItem
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              justifySelf: 'center',
              display: 'flex',
              width: 288,
              padding: '8px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              borderRadius: '4px',
              mt: 1,
              background: 'var(--background-paper-elevation-24, #FCFEFF)',
              boxShadow:
                '0 1px 18px 0 rgba(0,0,0,0.12), 0 6px 10px 0 rgba(0,0,0,0.14), 0 3px 5px -1px rgba(0,0,0,0.20)',
              '& .MuiMenuItem-root:hover': {
                backgroundColor: '#1976d20a',
              },
            },
          },
          list: {
            sx: {
              p: 0,
              width: '100%',
            },
          },
        }}
      >
        <MenuItem
          sx={{
            gap: '12px',
            padding: '8px',
          }}
          onClick={() => {
            setActivePage('organization');
            handleMenuClose();
          }}
        >
          <DomainRoundedIcon 
            sx={{
              color: theme =>
                theme.palette.grey[600]
            }}
          />
          <Typography>
            Organization profile
          </Typography>
        </MenuItem>
        {userOrgs.length > 1 ? (
          <div>
            <Divider
              sx={{
                backgroundColor: theme =>
                  theme.palette.divider,
              }}
            />
            <MenuItem
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px',
                backgroundColor: switchMenuOpen
                  ? theme => theme.palette.action.selected
                  : 'transparent',
              }}
              onClick={handleSwitchMenuOpen}
              selected={switchMenuOpen}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <SwapHorizRoundedIcon
                  sx={{
                    color: theme => theme.palette.grey[600],
                  }}
                />
                <Typography>Switch organization</Typography>
              </Box>
  
              {/* Right: chevron at flex-end */}
              <ChevronRightRoundedIcon 
                sx={{
                  color: theme => theme.palette.grey[600],
                }}
              />
            </MenuItem>
          </div>
        ) : (
          <></>
        )}
        {isSuperAdmin && (
          <>
            <Divider
              sx={{
                backgroundColor: theme =>
                  theme.palette.divider,
              }}
            />
            <MenuItem
              sx={{
                gap: '12px',
                padding: '8px',
              }}
              onClick={() => handleOrgCreate('new')}
            >
              <AddCircleOutlineRoundedIcon
                sx={{
                  color: theme =>
                    theme.palette.grey[600]
                }}
              />
              <Typography>
                Add new organization
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{
                gap: '12px',
                padding: '8px',
              }}
              onClick={() => handleOrgCreate('demo')}
            >
              <PlayCircleOutlineRoundedIcon
                sx={{
                  color: theme =>
                    theme.palette.grey[600]
                }}
              />
              <Typography>
                Create demo
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{
                gap: '12px',
                padding: '8px',
              }}
              onClick={() => {
                setActivePage('migrate-organization');
                handleMenuClose();
              }}
            >
              <LeakAddOutlinedIcon
                sx={{
                  color: theme =>
                    theme.palette.grey[600]
                }}
              />
              <Typography>
                Migrate Existing Organization
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{
                gap: '12px',
                padding: '8px',
              }}
              onClick={() => {
                setActivePage('celery-tasks');
                handleMenuClose();
              }}
            >
              <AccessTimeIcon 
                sx={{
                  color: theme =>
                    theme.palette.grey[600]
                }}
              />
              <Typography>
                Background Jobs
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{
                gap: '12px',
                padding: '8px',
              }}
              onClick={() => {
                setActivePage('supporters-merge');
                handleMenuClose();
              }}
            >
              <CallMergeIcon
                sx={{
                  color: theme =>
                    theme.palette.grey[600]
                }}
              />
              <Typography>
                Merge Supporters
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>
      <Menu
        sx={{
          display: 'flex',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          transform: 'translateX(-16px)',
        }}
        anchorEl={switchAnchorEl}
        open={Boolean(switchAnchorEl)}
        onClose={handleSwitchMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableAutoFocusItem
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              justifySelf: 'center',
              display: 'flex',
              minWidth: 280,
              maxWidth: 'fit-content',
              padding: '8px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              borderRadius: '4px',
              mt: 1,
              background: 'var(--background-paper-elevation-24, #FCFEFF)',
              boxShadow:
                '0 1px 18px 0 rgba(0,0,0,0.12), 0 6px 10px 0 rgba(0,0,0,0.14), 0 3px 5px -1px rgba(0,0,0,0.20)',
              '& .MuiMenuItem-root:hover': {
                backgroundColor: '#1976d20a',
              },
            },
          },
          list: {
            sx: {
              p: 0,
              width: '100%',
            },
          },
        }}
      >
        {userOrgs.length > 5 ? (
          <>
            {' '}
            <Paper
              component='form'
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: theme =>
                  theme.palette.neutrals.white,
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1, padding: '8px', }}
                placeholder='Search...'
                inputProps={{ 'aria-label': 'search by organization name' }}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={e => e.stopPropagation()}
              />
              <Divider 
                sx={{
                  backgroundColor: theme =>
                    theme.palette.divider,
                }}
              />
            </Paper>
          </>
        ) : (
          <></>
        )}
        {filteredOrgs.map((org, index) => (
          <MenuItem
            key={`${org.id}-${org.icon_url || 'none'}`}
            onClick={() => handleOrganizationSelect(org)}
            disableRipple
            sx={{
              width: '100%',
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom:
                index < filteredOrgs.length - 1
                  ? '1px solid rgba(0, 0, 0, 0.08)'
                  : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <OrgChipIcon organization={org} size='medium' />
              </Box>

              <Typography
                sx={{
                  pr: 5,
                  flexGrow: 1,
                  textAlign: 'left',
                }}
              >
                {org.name}
              </Typography>
            </Box>
            {/* Right: Check mark for selected */}
            {organization?.id === org.id ? (
              <CheckCircle
                sx={{
                  fontSize: 16,
                  color: theme => theme.palette.success.light, // $green-900
                }}
              />
            ) : (
              // Keeps alignment even when no check mark
              <Box sx={{ width: 22, height: 22 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
      {isSuperAdmin ? (
        <CreateOrganizationDialog
          isOpen={orgCreateDialog}
          creationType={creationType}
          onClose={() => setOrgCreateDialog(false)}
        />
      ) : (
        <></>
      )}
    </Box>
  );
};

export default ChangeOrganization;